import { getCopyQuery } from '../../data/sanity/queries';
import type {
  CopyQueryResult,
  HighlightedCampaignsCollection,
  SanityClientFetchOptions,
} from '../../data/sanity/types';
import { SanityServiceSimple } from '../../services/SanityServiceSimple/SanityServiceSimple';
import type { SemboMarket } from '../../utils/markets';
import {
  destinationsByMarket,
  topBarSpotlight,
  travelCategoriesByMarket,
} from '../MarketPage/MarketPage.queries';
import type {
  DestinationsByMarketQueryResult,
  GetTravelCategoriesByMarketResults,
  TopBarSpotlight,
} from '../MarketPage/MarketPage.types';
import {
  creatorBySlug,
  highlightedCampaignsCollectionByCreatorId,
  proudPartnersCreatorsLogos,
} from './CreatorPage.queries';
import type {
  CreatorBySlugQueryResult,
  GetCreatorBySlugResult,
  GetProudPartnersCreatorsLogosResult,
  ProudPartnersCreatorsLogosQueryResult,
} from './CreatorPage.types';

export const getCreatorBySlug = async (
  market: SemboMarket,
  usernameSlug: string,
  previewSettings: SanityClientFetchOptions
): Promise<GetCreatorBySlugResult> => {
  const result = await SanityServiceSimple.fetch<CreatorBySlugQueryResult>(
    creatorBySlug(market, usernameSlug),
    previewSettings,
    {
      source: 'getCreatorBySlug',
      params: { market: market.host, usernameSlug },
    }
  );

  if (!result) {
    return null;
  }

  return {
    ...result,
    username: usernameSlug,
  };
};

export const getBasicCreatorData = async (
  market: SemboMarket,
  previewSettings: SanityClientFetchOptions
): Promise<
  [
    CopyQueryResult,
    CopyQueryResult,
    GetProudPartnersCreatorsLogosResult,
    DestinationsByMarketQueryResult,
    TopBarSpotlight | null,
    GetTravelCategoriesByMarketResults,
  ]
> => {
  const result = await SanityServiceSimple.batch<
    [
      CopyQueryResult,
      CopyQueryResult,
      GetProudPartnersCreatorsLogosResult,
      DestinationsByMarketQueryResult,
      TopBarSpotlight | null,
      GetTravelCategoriesByMarketResults,
    ]
  >(
    [
      getCopyQuery('microcopy', undefined, market),
      getCopyQuery(
        'macrocopy',
        {
          include: [
            'explore.amelia.initialMessage',
            'explore.packageTrips.info',
            'packageTrips.protectionMessage',
          ],
        },
        market
      ),
      proudPartnersCreatorsLogos(market),
      destinationsByMarket(market),
      topBarSpotlight(market),
      travelCategoriesByMarket(market),
    ],
    previewSettings,
    {
      source: 'getBasicCreatorData',
      params: { market: market.host },
    }
  );

  return result;
};

export const getProudPartnersCreatorsLogos = async (
  market: SemboMarket,
  previewSettings: SanityClientFetchOptions
): Promise<GetProudPartnersCreatorsLogosResult> => {
  const result =
    await SanityServiceSimple.fetch<ProudPartnersCreatorsLogosQueryResult>(
      proudPartnersCreatorsLogos(market),
      previewSettings,
      {
        source: 'getProudPartnersCreatorsLogos',
        params: { market: market.host },
      }
    );

  return result ?? [];
};

export const getHighlightedCampaignsCollectionByCreatorId = async (
  market: SemboMarket,
  campaignId: string,
  previewSettings: SanityClientFetchOptions
): Promise<HighlightedCampaignsCollection> => {
  return SanityServiceSimple.fetch<HighlightedCampaignsCollection>(
    highlightedCampaignsCollectionByCreatorId(market, campaignId),
    previewSettings,
    {
      source: 'getHighlightedCampaignsCollectionByCreatorId',
      params: { market: market.host, campaignId },
    }
  );
};
